import { useEffect, useRef, useState } from 'react';
import mermaid from 'mermaid';

interface MermaidBlockProps {
  chart: string;
}

let mermaidId = 0;

// Get current theme based on system preference
function getTheme(): 'neutral' | 'dark' {
  if (typeof window !== 'undefined' && window.matchMedia('(prefers-color-scheme: dark)').matches) {
    return 'dark';
  }
  return 'neutral';
}

export function MermaidBlock({ chart }: MermaidBlockProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [svg, setSvg] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const [id] = useState(() => `mermaid-${++mermaidId}`);
  const [theme, setTheme] = useState<'neutral' | 'dark'>(getTheme);

  // Listen for theme changes
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = () => setTheme(getTheme());
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  useEffect(() => {
    const renderChart = async () => {
      if (!chart.trim()) {
        setSvg('');
        setError(null);
        return;
      }

      // Re-initialize mermaid with current theme
      mermaid.initialize({
        startOnLoad: false,
        theme,
        securityLevel: 'loose',
        fontFamily: 'ui-sans-serif, system-ui, sans-serif',
      });

      try {
        const { svg: renderedSvg } = await mermaid.render(id, chart.trim());
        setSvg(renderedSvg);
        setError(null);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to render diagram');
        setSvg('');
      }
    };

    renderChart();
  }, [chart, id, theme]);

  if (error) {
    return (
      <div
        style={{
          padding: '12px',
          backgroundColor: 'var(--negative-bg, #fef2f2)',
          border: '1px solid var(--status-error, #ef4444)',
          borderRadius: '4px',
          fontSize: '12px',
          color: 'var(--status-error, #ef4444)',
          fontFamily: 'ui-monospace, monospace',
          whiteSpace: 'pre-wrap',
        }}
      >
        <strong>Mermaid Error:</strong> {error}
        <pre style={{ marginTop: '8px', opacity: 0.7 }}>{chart}</pre>
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      style={{
        display: 'flex',
        justifyContent: 'center',
        padding: '16px',
        backgroundColor: 'var(--bg-muted)',
        borderRadius: '4px',
        overflow: 'auto',
      }}
      dangerouslySetInnerHTML={{ __html: svg }}
    />
  );
}
